import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Check, Minus, CircleCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { AppHeader } from "@/components/TopBar";
import { SectionTitle } from "@/components/shared";
import { moduleMeta } from "@/lib/icons";
import { adminApi, LS } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function SecurityOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem(LS.adminKey)) { navigate("/"); return; }
    adminApi.get("/admin/security")
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Admin key required to view security overview."); navigate("/"); });
  }, [navigate]);

  if (!data) return <div className="min-h-screen"><AppHeader /></div>;

  const modules = data.modules || [];
  const redData = Object.entries(data.redaction_by_type || {}).map(([k, v]) => ({ name: k, value: v })).sort((a, b) => b.value - a.value);
  const maxRed = redData[0]?.value || 1;

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader />
      <div className="flex-1 mx-auto w-full max-w-[1500px] px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-display font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" /> Security Overview
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Zero-exposure guarantees, sanitization analytics, and the tenant × module access matrix.</p>
        </div>

        {/* Guarantees */}
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="security-guarantees">
          {data.guarantees.map((g) => (
            <Card key={g.key} className="p-5 bg-card border-border flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/15 border border-primary/30 grid place-items-center shrink-0">
                <CircleCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium leading-5">{g.label}</div>
                <span className={cn(
                  "mt-2 inline-block rounded-full border px-2 py-0.5 text-[11px]",
                  g.status === "Enforced" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-blue-500/30 bg-blue-500/10 text-blue-300"
                )}>{g.status}</span>
              </div>
            </Card>
          ))}
        </div>

        {/* Redaction analytics */}
        <Card className="p-6 bg-card border-border mt-6" data-testid="redaction-analytics">
          <div className="flex items-center justify-between">
            <SectionTitle>Sanitization Analytics</SectionTitle>
            <span className="text-sm text-muted-foreground">Total redacted: <span className="font-display font-semibold text-rose-300 tabular-nums">{data.redactions.toLocaleString()}</span></span>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {redData.length === 0 && <div className="text-sm text-muted-foreground">No redactions recorded.</div>}
            {redData.map((r) => (
              <div key={r.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-mono text-muted-foreground">{r.name}</span>
                  <span className="tabular-nums">{r.value.toLocaleString()}</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-rose-400/70" style={{ width: `${(r.value / maxRed) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Access matrix */}
        <Card className="p-6 bg-card border-border mt-6">
          <SectionTitle>Tenant × Module Access Matrix</SectionTitle>
          <div className="mt-4">
            <ScrollArea className="w-full whitespace-nowrap">
              <table className="w-full text-sm border-collapse" data-testid="tenant-module-access-matrix">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-card text-left font-medium text-muted-foreground px-3 py-2 border-b border-border min-w-[180px]">Tenant</th>
                    {modules.map((m) => (
                      <th key={m.key} className="px-3 py-2 border-b border-border text-center min-w-[120px]">
                        <span className={cn("inline-flex items-center gap-1 text-[11px]", moduleMeta(m.key).color)}>
                          {m.name}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.access_matrix.map((row) => (
                    <tr key={row.tenant_id} className="hover:bg-secondary/40">
                      <td className="sticky left-0 z-10 bg-card font-medium px-3 py-2.5 border-b border-border/60">{row.tenant}</td>
                      {modules.map((m) => {
                        const on = row.modules[m.key];
                        return (
                          <td key={m.key} className="text-center px-3 py-2.5 border-b border-border/60" data-testid={`matrix-${row.tenant_id}-${m.key}`}>
                            {on ? (
                              <span className="inline-flex items-center gap-1 text-emerald-300"><Check className="h-3.5 w-3.5" /> <span className="text-[11px]">View</span></span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-muted-foreground/50"><Lock className="h-3 w-3" /> <span className="text-[11px]">None</span></span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Retrieval for each tenant is filtered strictly to the modules marked <span className="text-emerald-300">View</span>. Locked modules are never queried or returned.</p>
        </Card>
      </div>
    </div>
  );
}
